import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

/**
 * ErrorLogContext — Global error logger for the UTMS frontend.
 * 
 * Captures:
 *  - API errors (via logError calls from apiFetch or manual catches)
 *  - Connection errors (drone connect/disconnect failures)
 *  - WebSocket errors
 *  - Unhandled errors (window.onerror, unhandledrejection)
 *  - Custom application warnings/info
 * 
 * Each log entry has: id, timestamp, level, source, message, meta
 */

const MAX_ENTRIES = 500;

const ErrorLogContext = createContext(null);

let _globalLogError = null;
let _globalLogWarn = null;
let _globalLogInfo = null;

/**
 * Non-hook accessors for logging from outside React (e.g., apiFetch).
 * These are set when the provider mounts.
 */
export function getGlobalErrorLogger() {
  return {
    error: _globalLogError || (() => {}),
    warn: _globalLogWarn || (() => {}),
    info: _globalLogInfo || (() => {}),
  };
}

let _nextId = 1;

function createLogEntry(level, source, message, meta) {
  return {
    id: _nextId++,
    timestamp: new Date(),
    level,   // 'error' | 'warn' | 'info'
    source,  // e.g. 'API', 'WS', 'CONNECTION', 'FLIGHT', 'SYSTEM'
    message: String(message || 'Unknown error'),
    meta: meta || null,
  };
}

export function ErrorLogProvider({ children }) {
  const [entries, setEntries] = useState([]);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const addEntry = useCallback((level, source, message, meta) => {
    const entry = createLogEntry(level, source, message, meta);
    setEntries(prev => {
      const next = [...prev, entry];
      return next.length > MAX_ENTRIES ? next.slice(-MAX_ENTRIES) : next;
    });
    return entry;
  }, []);

  const logError = useCallback((source, message, meta) => addEntry('error', source, message, meta), [addEntry]);
  const logWarn = useCallback((source, message, meta) => addEntry('warn', source, message, meta), [addEntry]);
  const logInfo = useCallback((source, message, meta) => addEntry('info', source, message, meta), [addEntry]);

  const clearLogs = useCallback(() => setEntries([]), []);

  const clearByLevel = useCallback((level) => {
    setEntries(prev => prev.filter(e => e.level !== level));
  }, []);

  // Expose global accessors
  useEffect(() => {
    _globalLogError = logError;
    _globalLogWarn = logWarn;
    _globalLogInfo = logInfo;
    return () => {
      _globalLogError = null;
      _globalLogWarn = null;
      _globalLogInfo = null;
    };
  }, [logError, logWarn, logInfo]);

  // Catch unhandled errors
  useEffect(() => {
    const handleError = (event) => {
      const msg = event.message || (event.error && event.error.message) || 'Unhandled error';
      logError('SYSTEM', msg, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    const handleUnhandledRejection = (event) => {
      const reason = event.reason;
      const msg = reason instanceof Error ? reason.message : String(reason || 'Unhandled promise rejection');
      logError('SYSTEM', msg, { type: 'unhandledrejection' });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [logError]);

  const value = {
    entries,
    logError,
    logWarn,
    logInfo,
    clearLogs,
    clearByLevel,
  };

  return (
    <ErrorLogContext.Provider value={value}>
      {children}
    </ErrorLogContext.Provider>
  );
}

export function useErrorLog() {
  const ctx = useContext(ErrorLogContext);
  if (!ctx) {
    throw new Error('useErrorLog must be used within ErrorLogProvider');
  }
  return ctx;
}

export default ErrorLogContext;
